import Layout from "../components/Layout";
import { useRouter } from "@tanstack/react-router";
import { Flame, MapPin, Shield, Star, Target, Users, Zap } from "lucide-react";
import { motion } from "motion/react";
import LeadForm from "../components/LeadForm";

const benefits = [
  {
    icon: Zap,
    title: "Improve Coordination & Timing",
    desc: "Develop sharp reflexes and precise timing through structured pad work, drilling, and live rounds.",
  },
  {
    icon: Flame,
    title: "Burn Calories & Build Endurance",
    desc: "Muay Thai is one of the most effective full-body workouts — torch calories while developing real athletic conditioning.",
  },
  {
    icon: Target,
    title: "Learn Real Striking Skills",
    desc: "Master punches, kicks, elbows, and knees from a proven Muay Thai and kickboxing system.",
  },
  {
    icon: Shield,
    title: "Increase Confidence",
    desc: "Knowing you can handle yourself builds quiet confidence that carries into every area of your life.",
  },
];

const trainingSteps = [
  {
    number: "01",
    title: "Technique Instruction",
    desc: "Every class starts with focused technique work — punches, kicks, elbows, knees, and defensive footwork broken down clearly.",
  },
  {
    number: "02",
    title: "Pad Work & Drills",
    desc: "Build combinations and timing through structured pad work with partners and coaches. Each drill has a clear purpose.",
  },
  {
    number: "03",
    title: "Conditioning Rounds",
    desc: "Finish strong with conditioning work that builds the endurance and toughness you need to perform under pressure.",
  },
];

const audiences = [
  {
    icon: Star,
    title: "Beginners",
    desc: "No experience needed. We start from the fundamentals and build technique progressively so everyone can keep up.",
  },
  {
    icon: Flame,
    title: "Fitness-Focused Individuals",
    desc: "Train hard, burn serious calories, and build a strong, athletic body while learning real striking skills.",
  },
  {
    icon: Users,
    title: "Those Adding Striking to Grappling",
    desc: "Already training Jiu Jitsu or wrestling? Muay Thai adds a complete striking game to your overall martial arts skill set.",
  },
];

const serviceAreas = ["Eugene", "Thurston", "Glenwood", "Fairmont"];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const fadeLeft = (delay = 0) => ({
  initial: { opacity: 0, x: -30 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, delay },
});

const fadeRight = (delay = 0) => ({
  initial: { opacity: 0, x: 30 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, delay },
});

export default function MuayThaiPage() {
  const router = useRouter();

  return (
    <Layout>
      <title>
        Muay Thai Classes in Springfield, OR | Superhero Jiu Jitsu Academy
      </title>
      <meta
        name="description"
        content="Learn Muay Thai and striking at Superhero Jiu Jitsu Academy in Springfield, OR. Develop striking that translates into real-life confidence and resilience while training with a community that wants to see you improve every day."
      />

      {/* ── HERO SECTION ── */}
      <section
        className="relative min-h-[92vh] flex items-center"
        data-ocid="muay-thai-hero"
      >
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('/assets/images/muay thai hero image.jpg')",
          }}
          aria-hidden="true"
        />
        {/* Dark overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, oklch(0.10 0.06 255 / 0.97) 0%, oklch(0.14 0.07 270 / 0.88) 50%, oklch(0.12 0.05 255 / 0.70) 100%)",
          }}
          aria-hidden="true"
        />
        {/* Gold accent overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, oklch(0.68 0.16 82) 0%, transparent 50%)",
          }}
          aria-hidden="true"
        />
        {/* Gold top accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />

        <div className="relative container mx-auto px-4 sm:px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — copy */}
            <motion.div {...fadeLeft(0.1)}>
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
                Muay Thai · Springfield, OR
              </span>
              <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-hero-foreground leading-tight text-balance mb-5">
                Muay Thai &amp; Striking in{" "}
                <span className="text-gold">Springfield, OR</span>
              </h1>
              <p className="text-hero-foreground font-body text-lg leading-relaxed max-w-lg mb-8">
                Build speed, power, and precision while improving fitness and
                confidence.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  "Muay Thai",
                  "Kickboxing",
                  "Pad Work",
                  "Beginners Welcome",
                ].map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/20 border border-gold/40 text-sm font-body font-medium text-hero-foreground"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Right — lead capture form */}
            <motion.div {...fadeRight(0.2)}>
              <div
                className="bg-primary/80 backdrop-blur-sm border border-gold/30 rounded-2xl p-6 sm:p-8 shadow-elevated relative overflow-hidden"
                data-ocid="muay-thai-hero-form"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />
                <h2 className="font-display font-bold text-xl text-hero-foreground mb-1">
                  Book Your Free Class
                </h2>
                <p className="text-hero-foreground font-body text-sm mb-6 opacity-80">
                  First class is on us. No experience required. Must book in
                  advance.
                </p>

                <LeadForm />

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gold divider */}
      <div className="h-1 bg-gradient-accent" />

      {/* ── INTRO SECTION ── */}
      <section className="bg-background py-16 px-4" data-ocid="mt-intro">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeLeft(0.1)}>
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
                Speed · Power · Precision
              </span>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground leading-tight mb-6 text-balance">
                Striking That Builds You Up Inside and Out
              </h2>
              <div className="space-y-4 text-muted-foreground font-body text-base leading-relaxed">
                <p>
                  Muay Thai and striking at Superhero Jiu Jitsu Academy are
                  designed to help you move with speed, power, and precision.
                  You will learn practical skills through Muay Thai and
                  kickboxing while building the mindset to stay calm and sharp
                  under pressure.
                </p>
                <p>
                  Every session improves your timing and confidence and gives
                  you a challenging workout that leaves you stronger and more
                  prepared for whatever comes your way.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="relative rounded-2xl overflow-hidden shadow-elevated"
            >
              <img
                src="/assets/images/Superhero Jiu-Jitsu Acadmey_Muay Thai.jpg"
                alt="Instructor coaching a student during Jiu Jitsu class"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="mt-benefits"
      >
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div {...fadeUp(0.1)}>
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Real Results
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground mb-12 text-balance">
              Why Train Muay Thai?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map(({ icon: Icon, title, desc }, i) => (
              <motion.div key={title} {...fadeUp(0.1 + i * 0.1)}>
                <div className="bg-card rounded-2xl border border-border p-6 flex flex-col items-center text-center gap-4 shadow-card hover:shadow-elevated transition-smooth hover:-translate-y-0.5 h-full">
                  <span className="w-12 h-12 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                    <Icon className="w-5 h-5 text-foreground" />
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-base text-foreground mb-2">
                      {title}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground leading-snug">
                      {desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRAINING SECTION ── */}
      <section className="bg-hero py-16 px-4" data-ocid="mt-training">
        <div className="container mx-auto max-w-4xl">
          <motion.div {...fadeUp(0.1)} className="text-center mb-10">
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              How We Train
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-hero-foreground text-balance mb-4">
              Structured Classes. Real Skill.
            </h2>
            <p className="text-hero-foreground font-body text-base leading-relaxed max-w-2xl mx-auto opacity-80">
              Every Muay Thai class at Superhero Jiu Jitsu Academy is structured
              around technique, purposeful drilling, and conditioning. Coaches
              hold pads, correct your form, and push you to improve every single
              session. Whether you're brand new or looking to sharpen your game,
              each class gives you something to work on and something to build
              toward.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {trainingSteps.map(({ number, title, desc }, i) => (
              <motion.div key={number} {...fadeUp(0.15 + i * 0.1)}>
                <div className="bg-primary/60 backdrop-blur-sm border border-gold/20 rounded-2xl p-6 text-center h-full">
                  <span className="font-display font-black text-4xl text-gold opacity-60 block mb-3">
                    {number}
                  </span>
                  <h3 className="font-display font-bold text-base text-hero-foreground mb-3">
                    {title}
                  </h3>
                  <p className="font-body text-sm text-hero-foreground leading-relaxed opacity-80">
                    {desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            {...fadeUp(0.3)}
            className="text-hero-foreground font-body text-base leading-relaxed text-center max-w-2xl mx-auto opacity-80"
          >
            Our coaches actively hold pads and guide your development — this
            isn't a class where you just shadowbox in a corner. You will leave
            every session knowing you pushed harder and improved your skills.
          </motion.p>
        </div>
      </section>

      {/* ── WHO IT'S FOR SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="mt-who"
      >
        <div className="container mx-auto max-w-4xl">
          <motion.div {...fadeUp(0.1)} className="text-center mb-10">
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Everyone Welcome
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance mb-4">
              WHO IT'S FOR
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {audiences.map(({ icon: Icon, title, desc }, i) => (
              <motion.div key={title} {...fadeUp(0.15 + i * 0.1)}>
                <div className="bg-card rounded-2xl border border-border p-6 flex flex-col gap-4 shadow-card hover:shadow-elevated transition-smooth h-full">
                  <span className="w-11 h-11 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                    <Icon className="w-5 h-5 text-foreground" />
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-base text-foreground mb-2">
                      {title}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground leading-relaxed">
                      {desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section
        className="py-20 px-4 text-center relative"
        style={{
          background:
            "linear-gradient(135deg, oklch(0.12 0.06 255) 0%, oklch(0.18 0.08 270) 50%, oklch(0.14 0.05 255) 100%)",
        }}
        data-ocid="mt-cta"
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 50% 50%, oklch(0.68 0.16 82) 0%, transparent 60%)",
          }}
          aria-hidden="true"
        />
        <motion.div
          {...fadeUp(0.1)}
          className="relative container mx-auto max-w-2xl"
        >
          <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3 border border-gold/40 px-3 py-1 rounded-full">
            First Class Is Free
          </span>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight mb-4 text-balance">
            Start Your Muay Thai Journey Today
          </h2>
          <p className="text-hero-foreground font-body text-lg leading-relaxed mb-3 max-w-lg mx-auto">
            Your first class is free. No commitment, no experience required —
            just show up and see what you can do.
          </p>
          <p className="text-hero-foreground font-body text-sm mb-8 opacity-70">
            Must book in advance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={() => router.navigate({ to: "/contact-us" })}
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base bg-gradient-accent text-foreground shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth cursor-pointer"
              data-ocid="mt-cta-button"
            >
              Book Your Free Class
            </button>
            <a
              href="tel:+15416003371"
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base border-2 border-primary-foreground/60 text-hero-foreground hover:bg-primary-foreground/10 transition-smooth cursor-pointer"
              data-ocid="mt-cta-phone"
            >
              Call (541) 600-3371
            </a>
          </div>
        </motion.div>
      </section>

      {/* ── LOCAL SEO SECTION ── */}
      <section
        className="bg-background py-10 px-4 border-t border-border"
        data-ocid="mt-local-seo"
      >
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div {...fadeUp(0.1)}>
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
              <span className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                Serving Springfield &amp; Surrounding Areas
              </span>
            </div>
            <h2 className="font-display font-bold text-lg text-foreground mb-3">
              Muay Thai Classes in Springfield, OR
            </h2>
            <p className="font-body text-muted-foreground text-sm leading-relaxed max-w-2xl mx-auto mb-4">
              Serving Springfield, Eugene, Thurston, Glenwood, Fairmont, and
              surrounding Lane County areas.
            </p>
            <address className="not-italic font-body text-sm text-muted-foreground mb-5">
              <span className="font-medium text-foreground">
                1485 Mohawk Blvd, Springfield, OR 97477
              </span>
              {" · "}
              <a
                href="tel:+15416003371"
                className="text-foreground font-medium hover:text-gold transition-colors-fast"
              >
                (541) 600-3371
              </a>
              {" · "}
              <a
                href="mailto:shjjoffice@gmail.com"
                className="text-foreground font-medium hover:text-gold transition-colors-fast"
              >
                shjjoffice@gmail.com
              </a>
            </address>
            <div className="flex flex-wrap justify-center gap-2">
              {["Springfield", ...serviceAreas, "Lane County"].map((area) => (
                <span
                  key={area}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted border border-border text-sm font-body text-muted-foreground"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  {area}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
